import '../../../styles/pages/settings/settings.scss';
import '../../../styles/generic/normalForm.scss';
import '../../../styles/generic/confirmationModal.scss';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { useTranslation } from 'react-i18next';
import { HOME_ROUTE } from '../../../lib/constants/route';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import { useState, useEffect } from 'react';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import {
  settingSVG,
  shopSVG,
  agencySVG,
  warningSVG,
  warningWithCircleSVG,
  questionSVG,
} from '../../../lib/allCustomSVGs';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState } from '../../../store/store';
import { setIsSwitchShopPopupOpen } from '../../../store/reducers/uiSlice';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import {
  SHOP_API,
  USER_ROLE_API,
  APP_ENV,
  INTERCOM_ENABLED_ENVS,
} from '../../../lib/constants/central-api';
import { asyncHandler } from '../../../lib/api/async-handler';
import { updateProfile } from '../../../store/reducers/userSlice';
import { submitLogToBugsnag } from '../../../lib/api/log';
import Popup from '../../../ui/popup/Popup';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import HELP_LINKS from '../../../lib/constants/helpLinks';
import AISelectInput from '../../../ui/inputs/AISelectInput';

const ShopSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const shopSlug = useSelector(
    (state: RootState) => state.user.company.shop_slug
  );
  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const sellerToken = accessToken?.sellerToken;

  const userProfile = useSelector((state: RootState) => state.user.profile);
  const currentRole = userProfile?.role as 'seller' | 'agency';

  const [shopName, setShopName] = useState(
    userProfile.all_shops.find((shop) => shop.slug === shopSlug)?.name || ''
  );
  const [shopURL, setShopURL] = useState(
    userProfile.all_shops.find((shop) => shop.slug === shopSlug)?.webshop_url ||
      ''
  );
  const [currency, setCurrency] = useState('EUR');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submittingAgency, setSubmittingAgency] = useState<boolean>(false);
  const [
    isBecomeAgencyConfirmationPopupOpen,
    setIsBecomeAgencyConfirmationPopupOpen,
  ] = useState(false);
  const [loading, setLoading] = useState<'pending' | 'error' | 'success'>(
    'pending'
  );

  const taraSubscriptionDetails = useSelector(
    (state: RootState) => state.user?.shop?.taraSubscriptionDetails
  );

  const isDisabled = !shopName || !shopURL || shopName === '' || shopURL === '';

  useEffect(() => {
    const fetchShopSettings = async () => {
      try {
        setLoading('pending');
        const requestInfo: RequestInfo = {
          url: SHOP_API + `/${shopSlug}/settings`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `${accessToken?.type} ${sellerToken}`,
          },
        };

        const actionType = 'FETCH_SHOP_SETTINGS';
        const customMsg = '';

        const res: any = await asyncHandler(
          requestInfo,
          actionType,
          customMsg,
          false
        );

        const response = res?.data;

        if (
          res &&
          res?.actionType === actionType &&
          response &&
          response?.success === true &&
          response?.data?.general
        ) {
          const webshopUrlSetting = response.data.general.find(
            (item: any) => item.key === 'webshop_url'
          );
          const shopNameSetting = response.data.general.find(
            (item: any) => item.key === 'shop_name'
          );
          const currencySetting = response.data.general.find(
            (item: any) => item.key === 'currency'
          );

          if (webshopUrlSetting?.value) {
            setShopURL(webshopUrlSetting.value);
          }
          if (shopNameSetting?.value) {
            setShopName(shopNameSetting.value);
          }
          if (currencySetting?.value) {
            setCurrency(currencySetting.value);
          }

          setLoading('success');
        } else {
          setLoading('error');
        }
      } catch (exception) {
        console.log('Error fetching shop settings');
        submitLogToBugsnag('error', `${exception}`);
        setLoading('error');
      }
    };

    fetchShopSettings();
  }, [shopSlug, sellerToken]);

  const toggleBecomeAgencyConfirmationPopupModal = () => {
    setIsBecomeAgencyConfirmationPopupOpen(
      !isBecomeAgencyConfirmationPopupOpen
    );
  };

  async function submitAgency(e: any) {
    e.preventDefault();
    setSubmittingAgency(true);

    const requestInfo: RequestInfo = {
      url: `${USER_ROLE_API}`,
      method: 'PATCH',
      body: JSON.stringify({
        role: 'agency',
      }),
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
        Authorization: `${accessToken.type} ${sellerToken}`,
      },
    };

    const actionType = 'UPDATE_USER_ROLE';
    const customMsg = `${t('Agency-submit-success')}`;

    const res: any = await asyncHandler(
      requestInfo,
      actionType,
      customMsg,
      true
    );

    const response = res?.data;

    if (
      res &&
      res?.actionType === actionType &&
      response &&
      response?.success === true
    ) {
      if (response?.data?.roles.includes('agency')) {
        dispatch(
          updateProfile({
            role: 'agency',
          })
        );
        //* Trigger intercom event when the user account switches to an agency
        // @ts-ignore:next-line
        if (INTERCOM_ENABLED_ENVS.includes(APP_ENV) && window.Intercom) {
          console.log('INTERCOM Event Triggered: is-agency');
          // @ts-ignore:next-line
          window.Intercom('trackEvent', 'is-agency');
        }
        toggleBecomeAgencyConfirmationPopupModal();
      }
    }
    setSubmittingAgency(false);
  }

  async function onSubmitHandler(e: any) {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData: any = new FormData();
      formData.append(`_method`, 'put');
      formData.append('group', 'general');
      formData.append('shop_slug', shopSlug);
      formData.append('currency', currency);
      formData.append('shop_name', shopName);
      formData.append('webshop_url', shopURL);

      const requestInfo: RequestInfo = {
        url: SHOP_API + `/${shopSlug}/settings`,
        method: 'POST',
        // body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        body: formData,
        headers: {
          Accept: 'application/json',
          // 'Content-type': 'application/x-www-form-urlencoded',
          Authorization: accessToken.type + ' ' + sellerToken,
        },
      };

      const actionType = 'UPDATE_SHOP_GENERAL_SETTINGS';
      const customMsg = t('SettingsUpdatedSuccess-msg');

      const res: any = await asyncHandler(requestInfo, actionType, customMsg);
      setIsSubmitting(false);

      const response = res.data;

      if (res.actionType === actionType && response.success) {
        // Update only the specific shop with matching shopSlug
        const updatedShops = userProfile.all_shops.map((shop) => {
          if (shop.slug === shopSlug) {
            return {
              ...shop,
              name: shopName,
              webshop_url: shopURL,
            };
          }
          return shop;
        });

        store.dispatch(
          updateProfile({
            active_shop_slug: shopSlug,
            all_shops: updatedShops,
          })
        );
        return response.result;
      }
    } catch (exception) {
      console.log('Error during authentication request');
      setIsSubmitting(false);
      submitLogToBugsnag('error', `${exception}`);
    }
  }

  return (
    <SubPageInnerContentHolder
      title={t('Brands')}
      // subTitle={t('ManageProfileSettings')}
      backBtnLink={HOME_ROUTE}
      /* ------Help Button------ */
      mainButtonType='white'
      mainBtnTitle={t('Help')}
      mainBtnIcon={questionSVG}
      mainBtnLoading={false}
      mainBtnLink={HELP_LINKS.settings.brands || HELP_LINKS.settings.shops}
      isMainBtnLinkExternal={true}
    >
      {loading === 'pending' && <LoadingSpinner />}
      {loading === 'error' && <p>{t('ErrorFetchingData')}</p>}
      {loading === 'success' && (
        <>
          <div className='settingsActionCard settings_mobile-only-top-margin'>
            <div className='settingsActionCard__left'>
              <div className='settingsActionCard__left--icon'>
                <CustomSVGs svg={settingSVG} />
              </div>
              <div className='settingsActionCard__left--text'>
                <h2>
                  {currentRole === 'agency'
                    ? `${t('Agency-manage-brands')}`
                    : `${t('UpgradeToAgencyAccount')}`}
                </h2>
                <p>
                  {currentRole === 'agency'
                    ? `${t('Agency-manage-brands-description')}`
                    : `${t('ManageMultipleBrandsFromOneAccount')}`}
                </p>
              </div>
            </div>
            <div className='settingsActionCard__right'>
              <MainSquareButton
                buttonType='darkBlue'
                value={
                  currentRole === 'agency'
                    ? `${t('Agency-manage-brands')}`
                    : `${t('BecomeAgency')}`
                }
                icon={currentRole === 'agency' ? shopSVG : agencySVG}
                onClick={() => {
                  if (currentRole === 'agency') {
                    dispatch(setIsSwitchShopPopupOpen(true));
                  } else {
                    toggleBecomeAgencyConfirmationPopupModal();
                  }
                }}
              />
            </div>
          </div>

          <SettingsCommonCard
            mainTitle={t('CurrentBrandDetails')}
            subTitle={t('UpdateYourBrandInformation')}
          >
            <form id='shop-settings-form' onSubmit={onSubmitHandler}>
              <div className='normalForm'>
                <div className='normalForm__top'>
                  <div>
                    <AINormalInput
                      changeListeners={[
                        (e) => {
                          setShopName(e.target.value);
                        },
                      ]}
                      id='shopSettings-shop-name-input'
                      value={shopName}
                      placeholder={t('BrandName')}
                      required={true}
                      innerPlaceholder={t('EnterYourBrandName')}
                    />
                  </div>
                  <div>
                    <AINormalInput
                      changeListeners={[
                        (e) => {
                          setShopURL(e.target.value);
                        },
                      ]}
                      id='shopSettings-shop-url-input'
                      value={shopURL}
                      placeholder={t('BrandURL')}
                      required={true}
                      innerPlaceholder={t('EnterYourBrandURL')}
                    />
                  </div>
                  <div>
                    <AISelectInput
                      setSelect={(input: string) => {
                        setCurrency(input);
                      }}
                      id='shopSettings-currency-select'
                      defaultValue={currency}
                      placeholder={t('Currency Format')}
                      selectOptionArr={[
                        {
                          OptionValue: 'USD',
                          OptionName: 'Default (US)',
                        },
                        {
                          OptionValue: 'EUR',
                          OptionName: 'Europe',
                        },
                      ]}
                    />
                  </div>
                </div>
                <div
                  className='normalForm__bottom flex-start'
                  style={{ marginTop: '2rem' }}
                >
                  <MainSquareButton
                    value={t('UpdateBrandDetails')}
                    loading={isSubmitting}
                    disabled={isDisabled || isSubmitting}
                    type='submit'
                    form='shop-settings-form'
                  />
                </div>
              </div>
            </form>
          </SettingsCommonCard>
        </>
      )}

      <Popup
        styles={{
          backgroundColor: '#fff',
        }}
        setTrigger={toggleBecomeAgencyConfirmationPopupModal}
        trigger={isBecomeAgencyConfirmationPopupOpen}
      >
        <form
          id='shop-become-agency-confirmation-popup-form'
          onSubmit={submitAgency}
        >
          <div className='confirmationModal'>
            <div className='confirmationModal__title'>
              <CustomSVGs svg={warningSVG} />
              <h1>{t('Agency-switch-to-agency')}</h1>
            </div>
            <div className='confirmationModal__content'>
              <h3>{t('Agency-become-an-agency-confirmation-title')}</h3>
              {taraSubscriptionDetails &&
              taraSubscriptionDetails?.currentSubscriptionStatus ===
                'active' ? (
                <div className='confirmationModal__warning-box'>
                  <span className='confirmationModal__warning-icon'>
                    <CustomSVGs svg={warningWithCircleSVG} />
                  </span>
                  <div className='confirmationModal__warning-content'>
                    <div className='confirmationModal__warning-title'>
                      {t('Agency-become-an-agency-subscription-warning-title')}
                    </div>
                    <div>
                      {t(
                        'Agency-become-an-agency-subscription-warning-description'
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
              <div className='confirmationModal__actions'>
                <MainSquareButton
                  value={t('Cancel')}
                  type='button'
                  buttonType='white'
                  onClick={toggleBecomeAgencyConfirmationPopupModal}
                />
                <MainSquareButton
                  value={
                    submittingAgency
                      ? `${t('CreatingAgency')}...`
                      : `${t('Yes')}, ${t('Agency-become-an-agency')}`
                  }
                  type='submit'
                  disabled={submittingAgency}
                />
              </div>
            </div>
          </div>
        </form>
      </Popup>
    </SubPageInnerContentHolder>
  );
};

export default ShopSettings;
